import type { IntakeState, IntakeStep } from "@/lib/queries-intake";

/**
 * The intake as a list of steps a rep walks one at a time (ACL-403).
 *
 * **What this does and does not decide.** It decides what is on screen: which step, how far along
 * it reads, and whether Back and Skip are offered. It does *not* decide what comes next — that is
 * the engine's, recomputed from the pinned graph and the lead's current answers on every read
 * (ADR-110, the path is never stored). ACL-403 asks for exactly that: "the next question is
 * determined from the configured flow rather than hard-coded logic".
 *
 * The distinction matters because it is the one a wizard gets wrong. A conditional flow cannot
 * know its fifth question before the fourth is answered, so a frontend that walked the loaded
 * questions by itself would have to re-implement the condition evaluator — and two evaluators
 * judging state, firm and campaign rules will disagree eventually. The rep would then be shown a
 * question the engine did not choose, with answers saved against it. So `server.current` is the
 * only thing that moves the wizard forward; everything here is a view over the steps the server
 * already walked.
 *
 * Pure, so these rules are assertions in `test/intake-steps.test.ts` rather than something to
 * check by eye on a call.
 */

/** One stop on the walk, as the wizard draws it. */
export interface WalkStep {
  step: IntakeStep;
  /** 1-based over the questions only, so the numbers a rep reads have no gaps. A decision has
   *  none: it is a step of the route but not a question anybody answers. */
  position: number | null;
  /** Answered, skipped or passed: behind the rep, and reopenable. */
  done: boolean;
  /** The question the engine is asking now. */
  current: boolean;
  /** A step the flow settled for itself — a firm or campaign split. It sits in the list at the
   *  point the walk actually passed it, which is the only place it explains anything. */
  decision: boolean;
}

export interface Walk {
  /** Every step the engine has walked, in the order it walked them, current one last. */
  steps: WalkStep[];
  /** Where the rep is, or null when the run has settled or cannot be walked. */
  current: WalkStep | null;
  /** How many stops are behind them. Not a percentage: a conditional flow has no fixed length. */
  done: number;
  /** The server's own estimate of what is left, which shrinks as branches are resolved. */
  left: number;
  settled: boolean;
  problem: string | null;
}

const NOTHING: Walk = {
  steps: [],
  current: null,
  done: 0,
  left: 0,
  settled: false,
  problem: null,
};

/** A question somebody answers, as against a step the flow settled on its own. */
function isAsked(step: IntakeStep): boolean {
  return step.kind === "question";
}

/**
 * A step the flow decided for itself, kept in the walk.
 *
 * Only the splits. An `outcome` on the route is the flow's verdict, not a stop on the way to it,
 * and listing every `qualified` node the walk ran through would bury the questions.
 */
function isDecision(step: IntakeStep): boolean {
  return step.kind === "paths";
}

/**
 * The walk so far.
 *
 * `steps` arrives in route order and already carries the current question, so this keeps that
 * order rather than sorting: the engine walked them in it, and any other order would be a second
 * opinion about the route.
 */
export function walkOf(state: IntakeState | undefined | null): Walk {
  if (!state?.flow) return NOTHING;
  if (state.problem) return { ...NOTHING, problem: state.problem };

  const settled = state.status === "completed" || state.status === "rejected";
  const currentKey = state.current?.key ?? null;

  const steps: WalkStep[] = [];
  let asked = 0;
  for (const step of state.steps ?? []) {
    const decision = isDecision(step);
    // Questions and the splits between them, in the order the engine walked them. Keeping the
    // splits *in place* is the point: a firm split explains the questions that follow it, and
    // appending it after them (as this once did) puts the explanation after the thing explained.
    if (!isAsked(step) && !decision) continue;
    const current = step.key === currentKey;
    if (!decision) asked += 1;
    steps.push({
      step,
      position: decision ? null : asked,
      // The current question is not behind them even though the walk lists it.
      done: !current && step.status !== "current",
      current,
      decision,
    });
  }

  // The engine names the current question separately, and a reading can carry it without the
  // path; add it rather than leaving the wizard with nothing to ask.
  if (currentKey && !steps.some((one) => one.current) && state.current && isAsked(state.current)) {
    steps.push({ step: state.current, position: asked + 1, done: false, current: true, decision: false });
  }

  return {
    steps,
    current: steps.find((one) => one.current) ?? null,
    // Only questions are counted: "4 answered" means four answers, not four plus a split the
    // flow resolved on its own.
    done: steps.filter((one) => one.done && !one.decision).length,
    left: Math.max(0, state.steps_left ?? 0),
    settled,
    problem: null,
  };
}

/**
 * Whether the flow needs an answer to this step before it goes on (ACL-539).
 *
 * `must_answer` is the flow's rule, the one `runs.skip` refuses by. The field's own `required` is
 * never set on a seeded intake field, so reading only that offered Skip on every question and the
 * server refused each one. A step without `must_answer` comes from a server older than it and is
 * read as the flow reads a question with none: one that has to be answered.
 */
export function mustAnswer(step: IntakeStep): boolean {
  return step.required || step.must_answer !== false;
}

/**
 * Whether this step may be passed over.
 *
 * The server refuses a must-answer question with a 409 (`runs.skip`: "that question has to be
 * answered"), which is what actually enforces ACL-403's "rep cannot bypass required questions".
 * This only decides whether to *offer* the button: a Skip that always fails is a worse control
 * than no Skip at all.
 */
export function maySkip(step: IntakeStep | null | undefined): boolean {
  if (!step) return false;
  if (mustAnswer(step)) return false;
  // Nothing to skip past on a step this reader cannot answer anyway — somebody else must.
  return step.may_answer;
}

/**
 * The step a rep may reopen to change, if any.
 *
 * Answers already recorded can be changed — the engine re-walks and the route follows the new
 * answer (ADR-110) — but only where this reader may write the field.
 */
export function mayReopen(step: IntakeStep | null | undefined): boolean {
  return Boolean(step && step.may_answer && step.field_key);
}

/** How far along, in words. Never a percentage: a conditional flow has no fixed length. */
export function progressText(walk: Walk): string {
  if (walk.problem) return "This intake cannot be walked";
  if (walk.settled) return "All questions answered";
  if (!walk.current) return walk.done > 0 ? "Nothing more to ask" : "Not started";
  const at = `Question ${walk.current.position}`;
  // `steps_left` counts what the engine can still see ahead, which a branch may change. It is
  // shown as "more", never as a total, so it cannot read as a promise.
  return walk.left > 0 ? `${at} · ${walk.left} more` : at;
}

/**
 * What a step shows for the answer it holds.
 *
 * An option's **label**, never its stored key: the key is what a condition compares and what the
 * engine stores, so showing it would put `larry_h_parker` on a rep's screen.
 */
export function answerText(step: IntakeStep | null | undefined): string {
  if (!step) return "";
  const value = step.value;
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.map((one) => labelOf(step, one)).join(", ") || "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return labelOf(step, value);
}

function labelOf(step: IntakeStep, value: unknown): string {
  return step.options.find((one) => one.key === String(value))?.label ?? String(value);
}

/**
 * The call an intake panel should stay about, one render to the next (ACL-403).
 *
 * The question this answers is what to do at the hangup. The panel reads its lead from the live
 * ACD record while the call is up and from the stay still waiting for an outcome once it is over
 * (`softphone/intake-lead.ts`), and both are matched by the **call's own id** — so the id has to
 * outlive the call, or the panel blanks in the disposition window, which is the one moment a rep
 * is most likely to still be finishing questions.
 *
 * It is let go when the phone has no calls at all, the same moment the panel's own held lead is
 * cleared. Not on the call ending: that is precisely the gap this covers.
 *
 * Pure, so the rule is an assertion rather than something to catch by hanging up and looking.
 */
export function intakeCallId(
  current: string | null,
  held: string | null,
  callsOnPhone: number,
): string | null {
  if (current !== null) return current;
  return callsOnPhone === 0 ? null : held;
}

/**
 * Whether the intake panel is on the page at all, and whether the rep can see it (ACL-403).
 *
 * Two answers, not one, and keeping them apart is the whole point. The panel is the only thing
 * that can say whether this lead has a flow to walk — it alone reads the run — so the page learns
 * `hasIntake` *from* the panel. A page that waited for `hasIntake` before mounting the panel
 * therefore waits forever: nothing sets it, so no pane is offered, so no switch is drawn, so the
 * panel never mounts to set it. That deadlock shipped once (the switch and the questions were
 * both absent on `/phone`), which is why the rule is a function with a test rather than two
 * booleans inlined in the page.
 *
 * So `placed` ignores `hasIntake` entirely: it means "there is a call for the panel to be about",
 * which is the page's cue to mount a copy that draws nothing and does nothing but report. Only
 * `shown` consults `hasIntake` and the rep's own switch, and that is the copy with the questions
 * in it. `placed && !shown` is therefore the reporting copy's condition, and the page keeps one
 * or the other mounted at all times — never neither, which is the deadlock.
 */
export function intakePlacement(
  onIntakeCall: string | null,
  workShown: boolean,
  hasIntake: boolean,
  switchedOn: boolean,
): { placed: boolean; shown: boolean } {
  const placed = onIntakeCall !== null && workShown;
  return { placed, shown: placed && hasIntake && switchedOn };
}
